import { defineDocument } from '../../schema';
export const gameWildThingsDocument = defineDocument({
    id: 'game:wild-things',
    kind: 'game',
    slug: 'wild-things',
    route: '/discover/games/wild-things',
    seo: {
        title: 'Discover Wild Things',
        description: "Suggest a sighting, sketch a hybrid creature in shared segments, name the species, and see Poppy's photorealistic reveal in this cooperative party game.",
        imageAsset: 'coverWildThings',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Wild Things',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Spot something wild, sketch the hybrid creature together, and watch Poppy reveal what you found.',
            },
        ],
        imageAsset: 'coverWildThings',
        imageAlt: 'Wild Things game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "We're going on a nature expedition, and something weird is moving in the brush.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'First, the group suggests what they think they saw. Next, the group sketches the specimen together with each player handling one segment of the artwork. When the sketches are done, the group rightfully gets to name the new species they discovered.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Finally, once you're close enough, you will get a real glimpse of the wild thing that awaits you. No points up for grabs, just collective pride in furthering humanity's understanding of nature.",
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '2-16 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Cooperative',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free Input, Selection, Voting, Drawing',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~14 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'July 11, 2026',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '6a4443ab95150cffae4f482e',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Each round moves through four stages: suggesting what the group just spotted, collaboratively sketching the hybrid creature, naming the species, and revealing the finished Field Journal entry.',
                        },
                    ],
                },
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Everyone suggests what the group just spotted, combining write-in ideas with animal candidates Poppy seeds into the pool.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: "Players have two votes, with the two most popular sightings combining into the round's hybrid creature.",
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Players split into drawing groups of two to four people. Each group is assigned a segment of the creature, and every player draws their piece on adjoining canvases. A live mirror shows neighboring segments so players can more easily connect their drawings.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Once every group has finished sketching, everyone suggests and votes on a name for the new species. The most popular name is selected.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: "Poppy generates a photorealistic field journal photo of the creature, revealed beside additional field notes on the creature and every group's sketch.",
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Repeat for three rounds, logging a new specimen each time.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Wild Things is fully cooperative, so there's no scoring and no individual winner. The payoff is the finished Field Journal entry, and knowing you have advanced humanity's understanding of nature.",
                        },
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Sighting suggestions for what the group spotted',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Creature drawings',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Species name suggestions',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Sighting suggestions for what the group spotted',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Censorship screening of player sighting suggestions',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The final photorealistic Field Journal image',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Brain power required: low. It's about drawing together, not thinking hard.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: groups who want a laugh rather than a leaderboard, doodlers, large groups',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/wild-things-wanted',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Wild Things Wanted blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '6a4443ab95150cffae4f482e',
                },
            ],
        },
    ],
});
//# sourceMappingURL=wild-things.js.map